import type { FlagKey } from '@gozoki/feature-flags'
import { Navigate } from '@tanstack/react-router'
import { useContext, type PropsWithChildren } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { AuthContext } from '~/utils/context/auth.context'
import { usePermissionGuard, type PermissionRequirement } from '~/utils/rbac'
import { featureGuardState, rightsGuardState } from './route-guard-states'

/**
 * Gardes de rendu des pages du back-office, en un seul module :
 * - `FeatureRouteGuard` : page derrière un flag Flipt ;
 * - `RightsRouteGuard` : droit v2.
 * Les enfants ne sont montés (et leurs requêtes lancées) qu'une fois la garde passée.
 */

const Loading = () => <p className="p-4 text-sm text-muted-foreground">Chargement…</p>

/** Page derrière un flag : chargement tant que Flipt répond, flag OFF → retour à l'accueil. */
export function FeatureRouteGuard({ flag, children }: PropsWithChildren<{ flag: FlagKey }>) {
  const state = featureGuardState({
    flagsReady: useFeatureFlagsReady(),
    featureEnabled: useFeatureFlag(flag),
  })

  if (state === 'loading') return <Loading />
  if (state === 'redirect') return <Navigate to="/" replace />
  return <>{children}</>
}

/** Page gardée par un droit v2. */
export function RightsRouteGuard({
  permission,
  children,
}: PropsWithChildren<{ permission: PermissionRequirement }>) {
  const { isLoading } = useContext(AuthContext)
  const { authorized, missingPermissions } = usePermissionGuard(permission)
  const state = rightsGuardState({ authLoading: isLoading, authorized })

  if (state === 'loading') return <Loading />
  if (state === 'denied') return <RbacAccessDeniedPage missingPermissions={missingPermissions} />
  return <>{children}</>
}
